import { useState, useEffect, useCallback } from 'react';
import { useScore } from '../../../context/ScoreContext';
import { useSoundEffect } from '../../../hooks/useSoundEffect';

const NAV_LINKS = [
  { label: '最新消息', href: '#live',     icon: 'ri-signal-tower-line' },
  { label: 'PK 對戰',  href: '#pk',       icon: 'ri-sword-line'        },
  { label: '成員名冊', href: '#members',  icon: 'ri-group-line'         },
  { label: '冒險旅程', href: '#schedule', icon: 'ri-map-2-line'         },
  { label: '積分機制', href: '#score',    icon: 'ri-trophy-line'        },
];

interface NavBarProps {
  onOpenLottery: () => void;
  onOpenRating: () => void;
  onOpenMemoryWall: () => void;
}

export default function NavBar({ onOpenLottery, onOpenRating, onOpenMemoryWall }: NavBarProps) {
  const [scrolled, setScrolled]             = useState(false);
  const [menuOpen, setMenuOpen]             = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeSection, setActiveSection]   = useState('');
  const [pendingNav, setPendingNav]         = useState<string | null>(null);
  const [showAdminConfirm, setShowAdminConfirm] = useState(false);
  const { currentMember, setShowJudgePanel, scheduleState } = useScore();
  const ratingActive = scheduleState?.ratingActive ?? false;
  const { play } = useSoundEffect();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
      const docH = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(docH > 0 ? (window.scrollY / docH) * 100 : 0);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const ids = [...NAV_LINKS.map(l => l.href.slice(1))];
    const observers: IntersectionObserver[] = [];
    ids.forEach(id => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActiveSection(id); },
        { rootMargin: '-40% 0px -50% 0px', threshold: 0 },
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach(o => o.disconnect());
  }, []);

  // lock body scroll when menu open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const handleNav = (href: string) => {
    setMenuOpen(false);
    play('click');
    const el = document.querySelector(href);
    if (el) {
      const top = el.getBoundingClientRect().top + window.pageYOffset - 8;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const handleNavWithHighlight = useCallback((href: string) => {
    setPendingNav(href);
    play('click');
    setTimeout(() => {
      setMenuOpen(false);
      const el = document.querySelector(href);
      if (el) {
        const top = el.getBoundingClientRect().top + window.pageYOffset - 8;
        window.scrollTo({ top, behavior: 'smooth' });
      }
      setTimeout(() => setPendingNav(null), 600);
    }, 420);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setMenuOpen(false);
    play('click');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLottery = () => {
    setMenuOpen(false);
    play('lottery');
    onOpenLottery();
  };

  const handleRating = () => {
    setMenuOpen(false);
    play('click');
    onOpenRating();
  };

  const handleMemoryWall = () => {
    setMenuOpen(false);
    play('click');
    onOpenMemoryWall();
  };

  const handleAdminClick = () => {
    setMenuOpen(false);
    play('click');
    setShowAdminConfirm(true);
  };

  const handleAdminConfirm = () => {
    setShowAdminConfirm(false);
    play('unlock');
    setShowJudgePanel(true);
  };

  const teamColor   = currentMember?.team === 'black' ? '#d4a843' : '#c0c0c0';
  const teamLabel   = currentMember?.team === 'black' ? 'BLACK' : 'WHITE';
  const teamGlow    = currentMember?.team === 'black' ? 'rgba(212,168,67,0.3)' : 'rgba(192,192,192,0.25)';

  return (
    <>
      {/* ── Top bar ── */}
      <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-[#06060f]/96 backdrop-blur-md border-b border-[#d4a843]/15'
            : 'bg-transparent'
        }`}
      >
        {/* Scroll progress */}
        <div
          className="absolute bottom-0 left-0 h-[2px] transition-all duration-150 pointer-events-none z-10"
          style={{
            width: `${scrollProgress}%`,
            background: 'linear-gradient(90deg, #8a6010, #d4a843, #f0d060)',
            boxShadow: scrollProgress > 0 ? '0 0 6px rgba(212,168,67,0.7)' : 'none',
          }}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-3">

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-0.5 flex-1 justify-center">
            {NAV_LINKS.map(l => {
              const isActive = activeSection === l.href.slice(1);
              return (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={e => { e.preventDefault(); handleNav(l.href); }}
                  className="relative flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold tracking-wider transition-all duration-200 cursor-pointer whitespace-nowrap group"
                  style={{ color: isActive ? '#d4a843' : '#666' }}
                >
                  {isActive && (
                    <span
                      className="absolute inset-0 rounded-lg"
                      style={{ background: 'linear-gradient(135deg, #d4a84318, #d4a84308)', border: '1px solid #d4a84328' }}
                    />
                  )}
                  <i className={`${l.icon} text-sm relative z-10 transition-colors`} />
                  <span className="relative z-10">{l.label}</span>
                  {isActive && (
                    <span
                      className="absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 w-4 rounded-full"
                      style={{ background: '#d4a843', boxShadow: '0 0 6px #d4a843' }}
                    />
                  )}
                </a>
              );
            })}

            {/* Lottery */}
            <button
              onClick={handleLottery}
              className="relative flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-black tracking-wider cursor-pointer whitespace-nowrap overflow-hidden transition-all duration-200 ml-1"
              style={{
                background: 'linear-gradient(135deg, #d4a84322, #d4a84310)',
                border: '1px solid #d4a84340',
                color: '#d4a843',
                boxShadow: '0 0 12px rgba(212,168,67,0.1)',
              }}
            >
              <div className="absolute inset-0 opacity-30 pointer-events-none"
                style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)', animation: 'navShim 3s linear infinite' }} />
              <i className="ri-lottery-line text-sm relative z-10" />
              <span className="relative z-10">命運抽籤</span>
            </button>

            {/* Memory Wall button (desktop) */}
            <button
              onClick={handleMemoryWall}
              className="relative flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-black tracking-wider cursor-pointer whitespace-nowrap overflow-hidden transition-all duration-200"
              style={{
                background: 'linear-gradient(135deg, rgba(180,120,200,0.18), rgba(180,120,200,0.08))',
                border: '1px solid rgba(180,120,200,0.28)',
                color: '#c890e8',
                boxShadow: '0 0 12px rgba(180,120,200,0.1)',
              }}
            >
              <div className="absolute inset-0 opacity-30 pointer-events-none"
                style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)', animation: 'navShim 3.5s linear infinite' }} />
              <i className="ri-image-2-line text-sm relative z-10" />
              <span className="relative z-10">回憶牆</span>
            </button>

            {/* Rating button — only visible when active */}
            {ratingActive && (
              <button
                onClick={handleRating}
                className="relative flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-black tracking-wider cursor-pointer whitespace-nowrap overflow-hidden transition-all duration-200"
                style={{
                  background: 'linear-gradient(135deg, rgba(212,168,67,0.25), rgba(212,168,67,0.12))',
                  border: '1.5px solid rgba(212,168,67,0.6)',
                  color: '#d4a843',
                  boxShadow: '0 0 16px rgba(212,168,67,0.25)',
                  animation: 'ratingGlow 2s ease-in-out infinite',
                }}
              >
                <div className="absolute inset-0 opacity-30 pointer-events-none"
                  style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)', animation: 'navShim 2s linear infinite' }} />
                <i className="ri-star-fill text-sm relative z-10" />
                <span className="relative z-10">互評系統</span>
                <span className="relative z-10 w-1.5 h-1.5 rounded-full bg-[#d4a843] animate-ping" />
              </button>
            )}

            {/* 系統管理按鈕 (desktop) */}
            <button
              onClick={handleAdminClick}
              className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-black tracking-wider cursor-pointer transition-all duration-200 whitespace-nowrap"
              style={{
                background: 'rgba(60,60,70,0.4)',
                border: '1px solid rgba(100,100,110,0.3)',
                color: '#555',
              }}
            >
              <i className="ri-settings-3-line text-sm" />
              <span>系統管理</span>
            </button>
          </nav>

          {/* Right: member + hamburger */}
          <div className="flex items-center gap-2 shrink-0">
            {/* Member badge (desktop + sm) */}
            {currentMember && (
              <div
                className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold border"
                style={{ borderColor: `${teamColor}40`, background: `${teamColor}0d`, color: teamColor }}
              >
                {currentMember.avatarUrl ? (
                  <img src={currentMember.avatarUrl} alt="" className="w-5 h-5 rounded-full object-cover object-top shrink-0" style={{ border: `1px solid ${teamColor}50` }} />
                ) : (
                  <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black shrink-0" style={{ background: `${teamColor}22` }}>
                    {currentMember.name.charAt(0).toUpperCase()}
                  </span>
                )}
                <span className="max-w-[80px] truncate">{currentMember.name}</span>
                <span className="text-[9px] font-black tracking-widest opacity-60">{teamLabel}</span>
              </div>
            )}

            {/* Mobile hamburger */}
            <button
              className="md:hidden relative flex items-center gap-1.5 px-2.5 h-9 cursor-pointer rounded-xl transition-all"
              style={{
                background: menuOpen ? '#d4a84318' : 'transparent',
                border: menuOpen ? '1px solid #d4a84330' : '1px solid transparent',
              }}
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              {/* Outer ping ring — only when closed */}
              {!menuOpen && (
                <span
                  className="absolute inset-0 rounded-xl pointer-events-none"
                  style={{ animation: 'mapRing 2.4s ease-in-out infinite' }}
                />
              )}
              <i
                className={`ri-${menuOpen ? 'close' : 'treasure-map'}-line text-[#d4a843] text-2xl relative z-10`}
                style={
                  !menuOpen
                    ? { animation: 'mapGlow 2.4s ease-in-out infinite', filter: 'drop-shadow(0 0 6px #d4a843cc)' }
                    : undefined
                }
              />
              {!menuOpen && (
                <span
                  className="relative z-10 text-[#d4a843] text-xs font-black tracking-widest"
                  style={{ textShadow: '0 0 8px rgba(212,168,67,0.7)', fontFamily: "'Noto Sans TC', system-ui" }}
                >
                  地圖
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* ── Mobile full-screen drawer ── */}
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 md:hidden transition-all duration-300 ${menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
        onClick={() => setMenuOpen(false)}
      />

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 bottom-0 z-50 md:hidden w-[82vw] max-w-[320px] flex flex-col transition-transform duration-300 ${menuOpen ? 'translate-x-0' : 'translate-x-full'}`}
        style={{ background: 'linear-gradient(160deg, #0b0c1a 0%, #06060f 100%)', borderLeft: '1px solid #16172a' }}
      >
        {/* Panel header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#16172a]">
          <div className="flex items-center gap-2.5">
            <img
              src="https://public.readdy.ai/ai/img_res/c79b8da1-7d89-4197-bfec-80ebde63b2cd.png"
              alt=""
              className="w-7 h-7 object-contain"
            />
            <span className="text-[#d4a843] font-bold text-sm tracking-widest uppercase font-cinzel">靈魂共鳴賽</span>
          </div>
        </div>

        {/* Member card */}
        {currentMember && (
          <div className="mx-4 mt-4 p-3.5 rounded-2xl border relative overflow-hidden"
            style={{ background: `linear-gradient(135deg, ${teamColor}12, ${teamColor}04)`, borderColor: `${teamColor}30`, boxShadow: `0 0 20px ${teamGlow}` }}
          >
            <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${teamColor}60, transparent)` }} />
            <div className="flex items-center gap-3">
              {currentMember.avatarUrl ? (
                <img src={currentMember.avatarUrl} alt=""
                  className="w-11 h-11 rounded-full object-cover object-top shrink-0"
                  style={{ border: `2px solid ${teamColor}50` }}
                />
              ) : (
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center text-base font-black shrink-0"
                  style={{ background: `${teamColor}20`, color: teamColor, border: `2px solid ${teamColor}45` }}
                >
                  {currentMember.name.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-black truncate">{currentMember.name}</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span
                    className="text-[9px] px-2 py-0.5 rounded-full font-black tracking-widest uppercase"
                    style={{ background: `${teamColor}20`, color: teamColor, border: `1px solid ${teamColor}35` }}
                  >
                    {currentMember.team === 'black' ? '⬛ 黑隊' : '⬜ 白隊'}
                  </span>
                  <i className="ri-shield-check-line text-xs" style={{ color: `${teamColor}60` }} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Nav list */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1.5">
          <p className="text-[10px] font-black tracking-[0.3em] uppercase text-[#2a2a4a] mb-3 px-1">導航</p>

          {NAV_LINKS.map(l => {
            const isActive  = activeSection === l.href.slice(1);
            const isPending = pendingNav === l.href;
            return (
              <a
                key={l.href}
                href={l.href}
                onClick={e => { e.preventDefault(); if (!pendingNav) handleNavWithHighlight(l.href); }}
                className="flex items-center gap-3.5 px-4 py-3.5 rounded-xl cursor-pointer transition-all relative overflow-hidden select-none"
                style={{
                  background: isPending
                    ? 'linear-gradient(135deg, #d4a84335, #d4a84318)'
                    : isActive
                      ? 'linear-gradient(135deg, #d4a84318, #d4a84308)'
                      : '#0e0f1e',
                  border: `1px solid ${isPending ? '#d4a843aa' : isActive ? '#d4a84335' : '#16172a'}`,
                  color: isPending ? '#ffd970' : isActive ? '#d4a843' : '#555',
                  boxShadow: isPending ? '0 0 22px rgba(212,168,67,0.28), inset 0 0 16px rgba(212,168,67,0.08)' : 'none',
                  transform: isPending ? 'scale(1.025)' : 'scale(1)',
                  transition: 'all 0.2s cubic-bezier(0.34,1.56,0.64,1)',
                }}
              >
                {/* Left accent bar */}
                {(isActive || isPending) && (
                  <div className="absolute left-0 top-0 bottom-0 w-0.5 rounded-l-xl"
                    style={{ background: isPending ? 'linear-gradient(180deg, #ffd970, #d4a843)' : 'linear-gradient(180deg, #d4a843, #d4a84380)' }} />
                )}

                {/* Scan beam on pending */}
                {isPending && (
                  <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-xl">
                    <div
                      className="absolute top-0 bottom-0 w-16"
                      style={{
                        background: 'linear-gradient(90deg, transparent, rgba(212,168,67,0.35), transparent)',
                        animation: 'navScanBeam 0.42s linear forwards',
                      }}
                    />
                  </div>
                )}

                {/* Icon box */}
                <div
                  className="w-9 h-9 flex items-center justify-center rounded-xl shrink-0 transition-all duration-200"
                  style={{
                    background: isPending ? '#d4a84332' : isActive ? '#d4a84320' : '#151525',
                    border: `1px solid ${isPending ? '#d4a843aa' : isActive ? '#d4a84340' : '#1e1e32'}`,
                    boxShadow: isPending ? '0 0 12px rgba(212,168,67,0.5)' : 'none',
                  }}
                >
                  <i
                    className={`${l.icon} text-base`}
                    style={{
                      color: isPending ? '#ffd970' : isActive ? '#d4a843' : '#444',
                      filter: isPending ? 'drop-shadow(0 0 6px #d4a843)' : 'none',
                    }}
                  />
                </div>

                {/* Label */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold tracking-wide leading-tight">{l.label}</p>
                  {isPending && (
                    <p className="text-[10px] font-black tracking-[0.25em] mt-0.5"
                      style={{ color: '#d4a84380', animation: 'navDestFade 0.3s ease-out' }}>
                      前往中…
                    </p>
                  )}
                </div>

                {/* Right indicator */}
                {isPending ? (
                  <div className="shrink-0 flex items-center gap-1">
                    <div className="w-1 h-1 rounded-full bg-[#d4a843] animate-ping" style={{ animationDuration: '0.6s' }} />
                    <div className="w-1 h-1 rounded-full bg-[#d4a843] animate-ping" style={{ animationDuration: '0.6s', animationDelay: '0.1s' }} />
                    <div className="w-1 h-1 rounded-full bg-[#d4a843] animate-ping" style={{ animationDuration: '0.6s', animationDelay: '0.2s' }} />
                  </div>
                ) : isActive ? (
                  <i className="ri-arrow-right-s-line text-base ml-auto shrink-0" style={{ color: '#d4a84360' }} />
                ) : null}
              </a>
            );
          })}

          {/* Lottery — special */}
          <div className="pt-3 mt-1 border-t border-[#16172a]">
            <p className="text-[10px] font-black tracking-[0.3em] uppercase text-[#2a2a4a] mb-3 px-1">特殊功能</p>
            <button
              onClick={handleLottery}
              className="relative w-full flex items-center gap-3.5 px-4 py-3.5 rounded-xl cursor-pointer overflow-hidden transition-all"
              style={{
                background: 'linear-gradient(135deg, #d4a84322, #d4a84310)',
                border: '1px solid #d4a84440',
                color: '#d4a843',
                boxShadow: '0 0 16px rgba(212,168,67,0.12)',
              }}
            >
              <div className="absolute inset-0 opacity-20 pointer-events-none"
                style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)', animation: 'navShim 2.5s linear infinite' }} />
              <div className="w-9 h-9 flex items-center justify-center rounded-xl shrink-0 relative z-10"
                style={{ background: '#d4a84325', border: '1px solid #d4a84445' }}>
                <i className="ri-lottery-line text-base text-[#d4a843]" />
              </div>
              <div className="flex-1 text-left relative z-10">
                <p className="text-sm font-black tracking-wide">命運抽籤</p>
                <p className="text-[10px] text-[#d4a84380] tracking-wider mt-0.5">點選開始命運的輪盤</p>
              </div>
              <i className="ri-dice-line text-base relative z-10 opacity-60" />
            </button>

            {/* Memory Wall button (mobile drawer) */}
            <button
              onClick={handleMemoryWall}
              className="relative mt-2 w-full flex items-center gap-3.5 px-4 py-3.5 rounded-xl cursor-pointer overflow-hidden transition-all"
              style={{
                background: 'linear-gradient(135deg, rgba(180,120,200,0.18), rgba(180,120,200,0.08))',
                border: '1px solid rgba(180,120,200,0.28)',
                color: '#c890e8',
                boxShadow: '0 0 16px rgba(180,120,200,0.1)',
              }}
            >
              <div className="absolute inset-0 opacity-20 pointer-events-none"
                style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)', animation: 'navShim 3s linear infinite' }} />
              <div className="w-9 h-9 flex items-center justify-center rounded-xl shrink-0 relative z-10"
                style={{ background: 'rgba(180,120,200,0.18)', border: '1px solid rgba(180,120,200,0.32)' }}>
                <i className="ri-image-2-line text-base" style={{ color: '#c890e8' }} />
              </div>
              <div className="flex-1 text-left relative z-10">
                <p className="text-sm font-black tracking-wide">回憶牆</p>
                <p className="text-[10px] tracking-wider mt-0.5" style={{ color: 'rgba(200,144,232,0.55)' }}>記錄每一個精彩瞬間</p>
              </div>
              <i className="ri-gallery-line text-base relative z-10 opacity-60" />
            </button>

            {/* Rating button in mobile drawer */}
            {ratingActive && (
              <button
                onClick={handleRating}
                className="relative mt-2 w-full flex items-center gap-3.5 px-4 py-3.5 rounded-xl cursor-pointer overflow-hidden transition-all"
                style={{
                  background: 'linear-gradient(135deg, rgba(212,168,67,0.22), rgba(212,168,67,0.10))',
                  border: '1.5px solid rgba(212,168,67,0.55)',
                  color: '#d4a843',
                  boxShadow: '0 0 20px rgba(212,168,67,0.18)',
                }}
              >
                <div className="absolute inset-0 opacity-25 pointer-events-none"
                  style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)', animation: 'navShim 2s linear infinite' }} />
                <div className="w-9 h-9 flex items-center justify-center rounded-xl shrink-0 relative z-10"
                  style={{ background: 'rgba(212,168,67,0.25)', border: '1px solid rgba(212,168,67,0.5)' }}>
                  <i className="ri-star-fill text-base text-[#d4a843]" />
                </div>
                <div className="flex-1 text-left relative z-10">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-black tracking-wide">互評系統</p>
                    <span className="w-2 h-2 rounded-full bg-[#d4a843] animate-ping" />
                  </div>
                  <p className="text-[10px] text-[#d4a84370] tracking-wider mt-0.5">為對方隊伍評分！</p>
                </div>
                <i className="ri-star-smile-line text-base relative z-10 opacity-60" />
              </button>
            )}
          </div>
        </div>

        {/* Panel footer */}
        <div className="px-5 py-4 border-t border-[#16172a]">
          <div className="flex items-center justify-center gap-1.5">
            {[0, 150, 300, 450].map(d => (
              <div key={d} className="w-1 h-1 rounded-full"
                style={{ background: '#d4a84340', animation: `footerDot 1.8s ${d}ms infinite` }} />
            ))}
          </div>
          <p className="text-center text-[10px] text-[#2a2a4a] mt-2 tracking-widest">SOUL RESONANCE SERIES</p>

          {/* 系統管理 — 獨立灰色按鈕，置於最下方 */}
          <button
            onClick={handleAdminClick}
            className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl cursor-pointer transition-all duration-200 whitespace-nowrap"
            style={{
              background: 'rgba(30,30,40,0.6)',
              border: '1px solid rgba(80,80,90,0.25)',
              color: '#3a3a4a',
            }}
          >
            <i className="ri-settings-3-line text-sm" />
            <span className="text-xs font-bold tracking-widest">系統管理</span>
          </button>
        </div>
      </div>

      {/* ── 管理員確認彈窗 ── */}
      {showAdminConfirm && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-6"
          onClick={() => setShowAdminConfirm(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }} />

          {/* Dialog */}
          <div
            className="relative w-full max-w-xs rounded-2xl overflow-hidden"
            style={{ background: '#0e0e16', border: '1px solid rgba(120,120,140,0.25)' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Top accent line */}
            <div className="h-px w-full" style={{ background: 'linear-gradient(90deg, transparent, rgba(120,120,140,0.5), transparent)' }} />

            <div className="px-6 pt-6 pb-5 flex flex-col items-center text-center gap-4">
              {/* Icon */}
              <div className="w-14 h-14 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(80,80,100,0.2)', border: '1.5px solid rgba(100,100,120,0.3)' }}>
                <i className="ri-shield-keyhole-line text-2xl" style={{ color: '#666' }} />
              </div>

              {/* Text */}
              <div>
                <p className="text-white font-black text-base mb-2 tracking-wide">管理員專區</p>
                <p className="text-[#555] text-xs leading-relaxed">
                  此功能僅供<span className="text-[#888] font-bold">管理員</span>使用。<br />
                  如果你是玩家，請點擊取消。
                </p>
              </div>

              {/* Buttons */}
              <div className="flex gap-2.5 w-full pt-1">
                <button
                  onClick={() => setShowAdminConfirm(false)}
                  className="flex-1 py-2.5 rounded-xl text-sm font-bold cursor-pointer transition-all whitespace-nowrap"
                  style={{ background: '#1a1a24', border: '1px solid #2a2a38', color: '#666' }}
                >
                  取消
                </button>
                <button
                  onClick={handleAdminConfirm}
                  className="flex-1 py-2.5 rounded-xl text-sm font-bold cursor-pointer transition-all whitespace-nowrap"
                  style={{ background: 'rgba(60,60,80,0.8)', border: '1px solid rgba(100,100,120,0.4)', color: '#aaa' }}
                >
                  確認進入
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes navShim {
          from { transform: translateX(-200%); }
          to   { transform: translateX(200%); }
        }
        @keyframes footerDot {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50%       { opacity: 1;   transform: scale(1.5); background: #d4a843; }
        }
        @keyframes ratingGlow {
          0%, 100% { box-shadow: 0 0 12px rgba(212,168,67,0.25); }
          50%       { box-shadow: 0 0 24px rgba(212,168,67,0.55); }
        }
        @keyframes mapGlow {
          0%, 100% { filter: drop-shadow(0 0 4px #d4a843aa) drop-shadow(0 0 1px #d4a84366); opacity: 1; }
          50%       { filter: drop-shadow(0 0 10px #d4a843ff) drop-shadow(0 0 4px #d4a843cc); opacity: 0.85; }
        }
        @keyframes mapRing {
          0%        { box-shadow: 0 0 0 0px #d4a84340; opacity: 0.8; }
          70%       { box-shadow: 0 0 0 7px #d4a84300; opacity: 0; }
          100%      { box-shadow: 0 0 0 0px #d4a84300; opacity: 0; }
        }
        @keyframes navScanBeam {
          0%   { left: -4rem; opacity: 0; }
          15%  { opacity: 1; }
          100% { left: 110%; opacity: 0.4; }
        }
        @keyframes navDestFade {
          from { opacity: 0; transform: translateY(3px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
}